// Remote Config Keys - Matching Flutter implementation
// Source: centrajob_frontend_college/lib/utils/services/remote_config/remote_config_key.dart

// ==================== Type Definitions ====================
export type RemoteConfigValueType = 'boolean' | 'string' | 'list';

export interface RemoteConfigKeyDefinition {
  key: string;
  type: RemoteConfigValueType;
  defaultValue: boolean | string | string[];
}

// ==================== Boolean Keys ====================
export const BooleanKeys = {
  // Legacy Features
  enableGetAllCandidates: 'enableGetAllCandidates',
  enableCollegeJobsView: 'enableCollegeJobsView',
  enableCollegeManageCandidatesView: 'enableCollegeManageCandidatesView',
  enableCollegeCourseManagementView: 'enableCollegeCourseManagementView',
  enableUploadCollegeLogo: 'enableUploadCollegeLogo',
  enableCollegeStudentRequestsTab: 'enableCollegeStudentRequestsTab',
  enableCollegeNotifications: 'enableCollegeNotifications',
  enableCollegeOrTpoSettings: 'enableCollegeOrTpoSettings',

  // V2 Features
  enableV2AddCourse: 'enableV2AddCourse',
  enableV2ManageJobs: 'enableV2ManageJobs',
  enableV2ManageCourse: 'enableV2ManageCourse',
  enableV2ReportsAndAnalytics: 'enableV2ReportsAndAnalytics',
  enableV2HeaderActionButton: 'enableV2HeaderActionButton',
  enableV2ProfileSettings: 'enableV2ProfileSettings',
  enableV2TPOSettings: 'enableV2TPOSettings',
  enableV2ProfileSettingsPersonalInfo: 'enableV2ProfileSettingsPersonalInfo',
  enableV2ProfileSettingsCollegeDetails: 'enableV2ProfileSettingsCollegeDetails',
  enableV2ProfileSettingsKYC: 'enableV2ProfileSettingsKYC',
  enableV2ProfileSettingsPassword: 'enableV2ProfileSettingsPassword',
  enableV2ProfileSettingsTeam: 'enableV2ProfileSettingsTeam',
  enableV2ProfileSettingsNotification: 'enableV2ProfileSettingsNotification',
  enableV2ProfileSettingsHelp: 'enableV2ProfileSettingsHelp',

  // Manage Candidate Features
  enableV2ManageCandidateFilter: 'enableV2ManageCandidateFilter',
  enableV2ManageCandidateInviteCandidate: 'enableV2ManageCandidateInviteCandidate',
  enableV2ManageCandidateMoreButton: 'enableV2ManageCandidateMoreButton',
  enableV2ManageCandidateSearch: 'enableV2ManageCandidateSearch',
  enableManageCandidateStageTracking: 'enableManageCandidateStageTracking',
  enableManageCandidatesConsultationCall: 'enableManageCandidatesConsultationCall',
  enableManageCandidateGridView: 'enableManageCandidateGridView',

  // Manage Jobs Features
  enableV2ManageJobSearch: 'enableV2ManageJobSearch',
  enableV2CheckBoxSelectionManageJobs: 'enableV2CheckBoxSelectionManageJobs',
  enablev2JobTitlesFilterDropdownManageJobs: 'enablev2JobTitlesFilterDropdownManageJobs',
  enableV2ManageJobsShare: 'enableV2ManageJobsShare',
  enableV2ManageJobsComment: 'enableV2ManageJobsComment',
  enableV2ManageJobView: 'enableV2ManageJobView',
  enableV2ManageJobsViewIcon: 'enableV2ManageJobsViewIcon',
  enablev2JLocationFilterDropdownManageJobs: 'enablev2JLocationFilterDropdownManageJobs',
  enableManageJobApplicationTab: 'enableManageJobApplicationTab',
  enableManageJobGridView: 'enableManageJobGridView',

  // Dashboard Features
  enableV2DashboardDiscoverJobButton: 'enableV2DashboardDiscoverJobButton',
  enableV2DashboardInviteTeamMembersButton: 'enableV2DashboardInviteTeamMembersButton',

  // Manage Course Features
  enableV2ManageCourseDepartmentFilter: 'enableV2ManageCourseDepartmentFilter',
  enableV2ManageCourseEditCourse: 'enableV2ManageCourseEditCourse',
  enableV2ManageCourseShareCourse: 'enableV2ManageCourseShareCourse',
  enableV2ManageCourseDeleteCourse: 'enableV2ManageCourseDeleteCourse',
  enableV2ManageCourseDashboardListIcon: 'enableV2ManageCourseDashboardListIcon',
  enableV2ManageCourseDashboardTableIcon: 'enableV2ManageCourseDashboardTableIcon',
  enableV2ManageourseFilterColumn: 'enableV2ManageourseFilterColumn',
  enableV2ManageCourseSearchFunctionality: 'enableV2ManageCourseSearchFunctionality',

  // Other Features
  enableV2Announcements: 'enableV2Announcements',
  enableSemantics: 'enableSemantics',
  enableLearningModule: 'enableLearningModule',
  enableCandidateBatchLevelTracker: 'enableCandidateBatchLevelTracker',
  enableV2ProfileSettingsManageStageTracking: 'enableV2ProfileSettingsManageStageTracking',
  enableCreateStageButton: 'enableCreateStageButton',
  enableAddParameterButton: 'enableAddParameterButton',
  enableEventsCalendar: 'enableEventsCalendar',
  enableChatbot: 'enableChatbot',
  enableManageApplicationGridView: 'enableManageApplicationGridView',
  enableCandidateJobApplicationToggleForCollege: 'enableCandidateJobApplicationToggleForCollege',
  enableShareWhatsappMessageButton: 'enableShareWhatsappMessageButton',
  enableAddCandidateForJobApplication: 'enableAddCandidateForJobApplication',
  enableImportCandidateExcelForJobApplication: 'enableImportCandidateExcelForJobApplication',
} as const;

// ==================== String Keys ====================
export const StringKeys = {
  helpCenterImageSourceUrl: 'helpCenterImageSourceUrl',
  whatsappShareMessageTitle: 'whatsappShareMessageTitle',
  whatsappShareMessageSubTitle: 'whatsappShareMessageSubTitle',
  whatsappShareMessageAppPlaystoreLink: 'whatsappShareMessageAppPlaystoreLink',
  whatsappShareMessageAppIosLink: 'whatsappShareMessageAppIosLink',
  whatsappShareMessageCandidateWebUrl: 'whatsappShareMessageCandidateWebUrl',
} as const;

// ==================== List Keys (JSON string arrays) ====================
export const ListKeys = {
  learningMaterialStatusList: 'learningMaterialStatusList',
  learningMaterialDocumentTypeList: 'learningMaterialDocumentTypeList',
  learningMaterialCategoryList: 'learningMaterialCategoryList',
  batchLearningMaterialCategoryList: 'batchLearningMaterialCategoryList',
  learningMaterialTagsList: 'learningMaterialTagsList',
  learningMaterialBatchLevelList: 'learningMaterialBatchLevelList',
  v2JobTypeList: 'v2JobTypeList',
} as const;

// ==================== All Keys Combined ====================
export const RemoteConfigKeys = {
  ...BooleanKeys,
  ...StringKeys,
  ...ListKeys,
} as const;

// ==================== Default Values ====================
export const RemoteConfigDefaults: Record<string, boolean | string> = {
  // Boolean defaults - false
  [BooleanKeys.enableGetAllCandidates]: false,
  [BooleanKeys.enableCollegeJobsView]: false,
  [BooleanKeys.enableCollegeManageCandidatesView]: false,
  [BooleanKeys.enableCollegeCourseManagementView]: false,
  [BooleanKeys.enableUploadCollegeLogo]: false,
  [BooleanKeys.enableCollegeStudentRequestsTab]: false,
  [BooleanKeys.enableCollegeNotifications]: false,
  [BooleanKeys.enableCollegeOrTpoSettings]: false,
  [BooleanKeys.enableV2AddCourse]: false,
  [BooleanKeys.enableV2ManageJobs]: false,
  [BooleanKeys.enableV2ManageCourse]: false,
  [BooleanKeys.enableV2ReportsAndAnalytics]: false,
  [BooleanKeys.enableV2HeaderActionButton]: false,
  [BooleanKeys.enableV2ManageCandidateFilter]: false,
  [BooleanKeys.enableV2ManageCandidateInviteCandidate]: false,
  [BooleanKeys.enableV2ManageCandidateMoreButton]: false,
  [BooleanKeys.enableV2ManageCandidateSearch]: false,
  [BooleanKeys.enableV2ManageJobSearch]: false,
  [BooleanKeys.enableV2DashboardDiscoverJobButton]: false,
  [BooleanKeys.enableV2DashboardInviteTeamMembersButton]: false,
  [BooleanKeys.enableV2ManageCourseDepartmentFilter]: false,
  [BooleanKeys.enableV2ManageCourseEditCourse]: false,
  [BooleanKeys.enableV2ManageCourseShareCourse]: false,
  [BooleanKeys.enableV2ManageCourseDeleteCourse]: false,
  [BooleanKeys.enableV2ManageCourseDashboardListIcon]: false,
  [BooleanKeys.enableV2ManageCourseDashboardTableIcon]: false,
  [BooleanKeys.enableV2CheckBoxSelectionManageJobs]: false,
  [BooleanKeys.enablev2JobTitlesFilterDropdownManageJobs]: false,
  [BooleanKeys.enableV2ManageourseFilterColumn]: false,
  [BooleanKeys.enableV2ManageJobsShare]: false,
  [BooleanKeys.enableV2ManageJobsComment]: false,
  [BooleanKeys.enableV2ManageCourseSearchFunctionality]: false,
  [BooleanKeys.enableV2ManageJobView]: false,
  [BooleanKeys.enableV2ManageJobsViewIcon]: false,
  [BooleanKeys.enablev2JLocationFilterDropdownManageJobs]: false,
  [BooleanKeys.enableV2Announcements]: false,
  [BooleanKeys.enableSemantics]: false,
  [BooleanKeys.enableManageCandidateStageTracking]: false,
  [BooleanKeys.enableManageJobApplicationTab]: false,
  [BooleanKeys.enableLearningModule]: false,
  [BooleanKeys.enableCandidateBatchLevelTracker]: false,
  [BooleanKeys.enableV2ProfileSettingsManageStageTracking]: false,
  [BooleanKeys.enableCreateStageButton]: false,
  [BooleanKeys.enableAddParameterButton]: false,
  [BooleanKeys.enableManageCandidatesConsultationCall]: false,
  [BooleanKeys.enableEventsCalendar]: false,
  [BooleanKeys.enableChatbot]: false,
  [BooleanKeys.enableManageCandidateGridView]: false,
  [BooleanKeys.enableManageApplicationGridView]: false,
  [BooleanKeys.enableManageJobGridView]: false,
  [BooleanKeys.enableCandidateJobApplicationToggleForCollege]: false,
  [BooleanKeys.enableShareWhatsappMessageButton]: false,
  [BooleanKeys.enableAddCandidateForJobApplication]: true,
  [BooleanKeys.enableImportCandidateExcelForJobApplication]: false,

  // Boolean defaults - true
  [BooleanKeys.enableV2ProfileSettings]: true,
  [BooleanKeys.enableV2TPOSettings]: true,
  [BooleanKeys.enableV2ProfileSettingsPersonalInfo]: true,
  [BooleanKeys.enableV2ProfileSettingsCollegeDetails]: true,
  [BooleanKeys.enableV2ProfileSettingsKYC]: true,
  [BooleanKeys.enableV2ProfileSettingsPassword]: true,
  [BooleanKeys.enableV2ProfileSettingsTeam]: true,
  [BooleanKeys.enableV2ProfileSettingsNotification]: true,
  [BooleanKeys.enableV2ProfileSettingsHelp]: true,

  // String defaults - empty string
  [StringKeys.helpCenterImageSourceUrl]: '',
  [StringKeys.whatsappShareMessageTitle]: '',
  [StringKeys.whatsappShareMessageSubTitle]: '',
  [StringKeys.whatsappShareMessageAppPlaystoreLink]: '',
  [StringKeys.whatsappShareMessageAppIosLink]: '',
  [StringKeys.whatsappShareMessageCandidateWebUrl]: '',

  // List defaults - empty JSON array string
  [ListKeys.learningMaterialStatusList]: '[]',
  [ListKeys.learningMaterialDocumentTypeList]: '[]',
  [ListKeys.learningMaterialCategoryList]: '[]',
  [ListKeys.batchLearningMaterialCategoryList]: '[]',
  [ListKeys.learningMaterialTagsList]: '[]',
  [ListKeys.learningMaterialBatchLevelList]: '[]',
  [ListKeys.v2JobTypeList]: '[]',
};

// ==================== Type Guards ====================
export const isBooleanKey = (key: string): boolean => {
  return Object.values(BooleanKeys).includes(key as typeof BooleanKeys[keyof typeof BooleanKeys]);
};

export const isStringKey = (key: string): boolean => {
  return Object.values(StringKeys).includes(key as typeof StringKeys[keyof typeof StringKeys]);
};

export const isListKey = (key: string): boolean => {
  return Object.values(ListKeys).includes(key as typeof ListKeys[keyof typeof ListKeys]);
};

// ==================== Type Exports ====================
export type BooleanKeyType = typeof BooleanKeys[keyof typeof BooleanKeys];
export type StringKeyType = typeof StringKeys[keyof typeof StringKeys];
export type ListKeyType = typeof ListKeys[keyof typeof ListKeys];
export type RemoteConfigKeyType = typeof RemoteConfigKeys[keyof typeof RemoteConfigKeys];
